/**
 * Dashboard Layout Component
 * 
 * SECURITY: This layout displays organization-specific navigation.
 * Navigation items are filtered based on the user's organization.
 * 
 * - Nationwide Capital: Settlement dashboard navigation
 * - DigniFi: Welcome Agent dashboard navigation
 * - UnifiPro: Full navigation (admin access)
 */

import { useClerk, useUser } from "@clerk/clerk-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/useMobile";
import { useOrganizationContext } from "@/hooks/useOrganization";
import { 
  LayoutDashboard, 
  PanelLeft, 
  Users, 
  Briefcase, 
  Phone, 
  FileText, 
  LogOut, 
  Home,
  TrendingUp,
  Building,
  Shield,
  BarChart3,
  ThumbsUp,
  FileCheck,
  CreditCard,
  Bot,
  Settings,
  Megaphone,
  ClipboardList
} from "lucide-react";
import { CSSProperties, useEffect, useRef, useState, useMemo } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

// Icon mapping for dynamic navigation
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  Users,
  Briefcase,
  Phone,
  FileText,
  TrendingUp,
  Building,
  Megaphone,
};

// Settlement Dashboard navigation items
const settlementMenuItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/" },
  { icon: Briefcase, label: "Placements", path: "/placements" },
  { icon: Phone, label: "AI Agent", path: "/agent" },
  { icon: Users, label: "Accounts", path: "/accounts" },
  { icon: FileText, label: "Call Records", path: "/calls" },
  { icon: CreditCard, label: "Payments", path: "/payments" },
  { icon: Bot, label: "Eva", path: "/eva" },
];

// Welcome Agent Dashboard navigation items
const welcomeAgentMenuItems = [
  { icon: LayoutDashboard, label: "Overview", path: "/" },
  { icon: ClipboardList, label: "Loan Servicing", path: "/servicing" },
  { icon: Megaphone, label: "Campaigns", path: "/campaigns" },
  { icon: TrendingUp, label: "FPD Insights", path: "/fpd" },
  { icon: BarChart3, label: "FPD Stats", path: "/fpd-stats" },
  { icon: Building, label: "Dealer Performance", path: "/dealers" },
  { icon: ThumbsUp, label: "Positive Feedback", path: "/feedback" },
  // { icon: FileCheck, label: "Compliance", path: "/compliance" },  // Hidden temporarily
  { icon: Settings, label: "Call Processing", path: "/call-processing" },
  { icon: Bot, label: "Eva", path: "/eva" },
];

const SIDEBAR_WIDTH_KEY = "sidebar-width";
const DEFAULT_WIDTH = 280;
const MIN_WIDTH = 200;
const MAX_WIDTH = 480;

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": `${sidebarWidth}px`,
        } as CSSProperties
      }
    >
      <DashboardLayoutContent setSidebarWidth={setSidebarWidth}>
        {children}
      </DashboardLayoutContent>
    </SidebarProvider>
  );
}

type DashboardLayoutContentProps = {
  children: React.ReactNode;
  setSidebarWidth: (width: number) => void;
};

function DashboardLayoutContent({
  children,
  setSidebarWidth,
}: DashboardLayoutContentProps) {
  const [location, setLocation] = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Get user info from Clerk
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  
  // Get organization context for navigation
  const { dashboardType, organizationName, features, isAdmin, loanServicingOnly } = useOrganizationContext();

  // Determine which menu items to show based on organization and admin status
  const menuItems = useMemo(() => {
    if (dashboardType === 'welcome_agent') {
      if (loanServicingOnly) {
        // Loan Servicing-only orgs only see the Loan Servicing tab
        return welcomeAgentMenuItems.filter(item => item.path === '/servicing');
      }
      if (!isAdmin) {
        // Hide admin-only pages from regular users
        const adminOnlyPaths = ['/campaigns', '/eva', '/call-processing'];
        return welcomeAgentMenuItems.filter(item => !adminOnlyPaths.includes(item.path));
      }
      return welcomeAgentMenuItems;
    }
    return settlementMenuItems;
  }, [dashboardType, isAdmin, loanServicingOnly]);

  const activeMenuItem = menuItems.find(item => item.path === location);

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success("Logged out successfully");
    } catch (error) {
      toast.error("Failed to logout");
    }
  };

  useEffect(() => {
    if (isCollapsed) {
      setIsResizing(false);
    }
  }, [isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const sidebarLeft = sidebarRef.current?.getBoundingClientRect().left ?? 0;
      const newWidth = e.clientX - sidebarLeft;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setSidebarWidth]);

  // Get user initials from Clerk user
  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user?.firstName) {
      return user.firstName.slice(0, 2).toUpperCase();
    }
    if (user?.primaryEmailAddress?.emailAddress) {
      return user.primaryEmailAddress.emailAddress.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  const getUserName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.firstName) {
      return user.firstName;
    }
    return 'User';
  };

  const getUserEmail = () => {
    return user?.primaryEmailAddress?.emailAddress || '';
  };

  return (
    <>
      <div className="relative" ref={sidebarRef}>
        <Sidebar
          collapsible="icon"
          className="border-r-0"
          disableTransition={isResizing}
        >
          <SidebarHeader className="h-auto py-4 justify-center">
            <div className="flex flex-col gap-2 px-2 transition-all w-full">
              {/* Organization Badge */}
              {!isCollapsed && organizationName && (
                <div className="flex items-center gap-2 px-1 py-1">
                  <div className="h-8 w-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                    <span className="text-primary font-bold text-sm">
                      {organizationName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="font-semibold text-sm truncate">{organizationName}</span>
                    <span className="text-xs text-muted-foreground">
                      {dashboardType === 'welcome_agent' ? 'Welcome Agent' : 'Settlement'}
                    </span>
                  </div>
                  {isAdmin && (
                    <Badge variant="outline" className="ml-auto text-xs shrink-0">
                      <Shield className="h-3 w-3 mr-1" />
                      Admin
                    </Badge>
                  )}
                </div>
              )}
              
              {/* Toggle Button */}
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleSidebar}
                  className="h-8 w-8 flex items-center justify-center hover:bg-accent rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0"
                  aria-label="Toggle navigation"
                >
                  <PanelLeft className="h-4 w-4 text-muted-foreground" />
                </button>
                {!isCollapsed ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold tracking-tight truncate">
                      Navigation
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </SidebarHeader>

          <SidebarContent className="gap-0">
            <SidebarMenu className="px-2 py-1">
              {menuItems.map(item => {
                const isActive = location === item.path;
                return (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton
                      isActive={isActive}
                      onClick={() => setLocation(item.path)}
                      tooltip={item.label}
                      className={`h-10 transition-all font-normal`}
                    >
                      <item.icon
                        className={`h-4 w-4 ${isActive ? "text-primary" : ""}`}
                      />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
            {/* Investor Admin — admin only, links to investor management */}
            {isAdmin && (
              <SidebarMenu className="px-2 py-1 border-t border-border mt-1 pt-2">
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={location === '/investor-admin'}
                    onClick={() => setLocation('/investor-admin')}
                    tooltip="Investor Management"
                    className="h-10 transition-all font-normal"
                  >
                    <TrendingUp
                      className={`h-4 w-4 ${location === '/investor-admin' ? 'text-primary' : ''}`}
                    />
                    <span>Investor Management</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            )}
          </SidebarContent>

          <SidebarFooter className="p-3">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3 rounded-lg px-1 py-1 w-full text-left group-data-[collapsible=icon]:justify-center">
                <Avatar className="h-9 w-9 border shrink-0">
                  {user?.imageUrl && <AvatarImage src={user.imageUrl} alt={getUserName()} />}
                  <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                  <p className="text-sm font-medium truncate leading-none">
                    {getUserName()}
                  </p>
                  <p className="text-xs text-muted-foreground truncate mt-1.5">
                    {getUserEmail()}
                  </p>
                </div>
              </div>
              {!isCollapsed && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-muted-foreground hover:text-foreground"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              )}
            </div>
          </SidebarFooter>

          {/* Resize handle */}
          {!isCollapsed && !isMobile && (
            <div
              className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/20 transition-colors"
              onMouseDown={() => setIsResizing(true)}
            />
          )}
        </Sidebar>
      </div>

      <SidebarInset className="flex flex-col min-h-screen">
        <header className="sticky top-0 z-10 flex h-14 items-center gap-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4">
          <SidebarTrigger className="md:hidden" />
          <div className="flex items-center gap-2">
            <Home className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">/</span>
            <span className="text-sm font-medium">
              {activeMenuItem?.label || "Dashboard"}
            </span>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </SidebarInset>
    </>
  );
}
